import { createActionFactory, Feature, createThunk, ThunkDispatch } from '../../../shared';
import type { HubPersona } from '../../selectors/currentPersonaSelector';
import { moveContacts, handleMoveContactsError } from '../data/moveContactsDataActions';
import type { Origin } from '../../state/moveContactsState';
import { getDisplayedItemHubPersonaIds } from '../../selectors/personaListSelector';
import { getPackageState, appStatePackageStateKey, AppState } from '../../../appstate';
import { newFolderEntered } from '../../../appchrome';
import { HubState, packageStateKey } from './../../state';

export interface MoveContactsActionPayload {
    personas: HubPersona[];
    fromFolderId: string;
    toFolderId: string;
    displayedHubPersonaIds: string[];
    origin: Origin;
    isMoveInProgress: boolean;
}

export const moveContactsClickedAction = createActionFactory<MoveContactsActionPayload>(
    'MoveContactsClickedAction',
    Feature.MoveContacts,
    action => {
        return {
            origin: action.payload.origin,
        };
    }
);

export const moveContactsClicked = (
    personas: HubPersona[],
    fromFolderId: string,
    toFolderId: string,
    origin: Origin
) =>
    createThunk('moveContactsClicked', (dispatch: ThunkDispatch, getState: () => {}) => {
        const state = getPackageState<HubState>(getState(), packageStateKey);
        const displayedHubPersonaIds = getDisplayedItemHubPersonaIds(
            state.appState,
            state.packageState
        );

        const actionPayload: MoveContactsActionPayload = {
            personas,
            fromFolderId,
            toFolderId,
            displayedHubPersonaIds,
            origin,
            isMoveInProgress: true,
        };
        dispatch(moveContactsClickedAction(actionPayload));
        dispatch(moveContacts(actionPayload));
    });

export const createFolderAndMoveContactsClicked = (
    folderName: string,
    personas: HubPersona[],
    fromFolderId: string,
    rootFolderId: string
) =>
    createThunk(
        'CreateFolderAndMoveContactsClicked',
        async (dispatch: ThunkDispatch, getState: () => {}) => {
            const state = getPackageState<HubState>(getState(), packageStateKey);
            const displayedHubPersonaIds = getDisplayedItemHubPersonaIds(
                state.appState,
                state.packageState
            );

            const actionPayload: MoveContactsActionPayload = {
                personas,
                fromFolderId,
                toFolderId: '',
                displayedHubPersonaIds,
                origin: 'CommandBar',
                isMoveInProgress: true,
            };
            // We don't have folder Id yet here, but we already want to remove elements from HubList
            // since it will trigger the CommandBar to close
            // Passing payload without toFolderId will optimistically remove items from HubList,
            // and as soon as we get FolderId we need to update actionPayload
            dispatch(moveContactsClickedAction(actionPayload));

            const newFolder = await newFolderEntered(dispatch)(folderName, rootFolderId, true);

            if (newFolder) {
                if ('alreadyExists' in newFolder) {
                    const { displayedContactFolders } = getPackageState<AppState>(
                        getState(),
                        appStatePackageStateKey
                    ).appState.data.contactFoldersState;

                    const existingFolder = displayedContactFolders.find(
                        folder => folder.displayName === folderName
                    );

                    if (existingFolder) {
                        dispatch(moveContacts({ ...actionPayload, toFolderId: existingFolder.id }));
                    } else {
                        handleMoveContactsError(
                            dispatch,
                            Error("Error during moveContacts, Can't create folder with this name"),
                            actionPayload
                        );
                    }
                } else {
                    dispatch(moveContacts({ ...actionPayload, toFolderId: newFolder.id }));
                }
            }
        }
    );
export type { Action } from '../../../shared';
